import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, getMl } from "@/lib/api";
import MultilingualField from "@/components/MultilingualField";
import FileUploader from "@/components/FileUploader";

const empty = () => ({
  name: "",
  position: { en: "", tr: "", id: "" },
  bio: { en: "", tr: "", id: "" },
  photo_url: "",
  email: "",
  social_links: { linkedin: "", instagram: "", whatsapp: "" },
  sort_order: 0,
  is_published: true,
});

const AdminTeam = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/team?published_only=false").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onSave = async (data) => {
    if (!data.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      if (editing?.id) await api.put(`/team/${editing.id}`, data);
      else await api.post("/team", data);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || ""));
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Remove this team member?")) return;
    await api.delete(`/team/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div data-testid="admin-team">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-display text-3xl text-[#0A1128]">Leadership Team</h1>
          <p className="text-slate-500 text-sm mt-1">CEO, Directors, COO and other key team members shown on the About page.</p>
        </div>
        <button onClick={() => setEditing(empty())} data-testid="add-team-btn" className="btn-gold">
          <Plus size={14} /> Add Member
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-500">
          No team members yet. Add your first leader to display them on the About page.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((m) => (
            <div key={m.id} data-testid={`team-row-${m.id}`} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="aspect-[4/5] bg-slate-100 relative">
                {m.photo_url ? (
                  <img src={fileUrl(m.photo_url)} alt={m.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl text-slate-300 font-display">
                    {m.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </div>
                )}
                {!m.is_published && (
                  <div className="absolute top-2 left-2 bg-slate-800/90 text-white text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm flex items-center gap-1">
                    <EyeOff size={10} /> Hidden
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1">{getMl(m.position, "en") || "—"}</div>
                <h3 className="font-display text-lg text-[#0A1128] mb-3">{m.name}</h3>
                <div className="flex justify-end gap-1">
                  <button onClick={() => setEditing(m)} data-testid={`edit-team-${m.id}`} className="p-2 text-slate-400 hover:text-[#0A1128]"><Pencil size={14} /></button>
                  <button onClick={() => onDelete(m.id)} data-testid={`del-team-${m.id}`} className="p-2 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-6 overflow-y-auto" data-testid="team-form-modal">
          <div className="bg-white max-w-2xl w-full rounded-sm my-10">
            <div className="flex justify-between items-center p-6 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="font-display text-2xl">{editing.id ? "Edit" : "Add"} Team Member</h2>
              <button onClick={() => setEditing(null)} data-testid="close-team-form"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-5">
              <FileUploader
                label="Photo (portrait, recommended 4:5 ratio)"
                value={editing.photo_url}
                onChange={(v) => setEditing({ ...editing, photo_url: v })}
                testId="team-photo"
              />

              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Full Name *</label>
                <input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="e.g. Ferhat Yıldız"
                  required
                  data-testid="team-name"
                  className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm"
                />
              </div>

              <MultilingualField
                label="Position / Title (e.g. CEO, Director, COO)"
                value={editing.position}
                onChange={(v) => setEditing({ ...editing, position: v })}
                testId="team-position"
                required
              />

              <MultilingualField
                label="Bio / Short Description"
                value={editing.bio}
                onChange={(v) => setEditing({ ...editing, bio: v })}
                testId="team-bio"
                multiline
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Email</label>
                  <input
                    type="email"
                    value={editing.email || ""}
                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                    data-testid="team-email"
                    className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Sort Order</label>
                  <input
                    type="number"
                    value={editing.sort_order}
                    onChange={(e) => setEditing({ ...editing, sort_order: +e.target.value })}
                    data-testid="team-sort"
                    className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-3">Social Links</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { k: "linkedin", ph: "https://linkedin.com/in/..." },
                    { k: "instagram", ph: "https://instagram.com/..." },
                    { k: "whatsapp", ph: "+62..." },
                  ].map(({ k, ph }) => (
                    <input
                      key={k}
                      placeholder={ph}
                      value={editing.social_links?.[k] || ""}
                      onChange={(e) => setEditing({ ...editing, social_links: { ...editing.social_links, [k]: e.target.value } })}
                      data-testid={`team-social-${k}`}
                      className="px-3 py-2 border border-slate-300 rounded-sm text-sm"
                    />
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editing.is_published}
                  onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })}
                  data-testid="team-published"
                />
                Published (visible on About page)
              </label>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-slate-200 sticky bottom-0 bg-white">
              <button onClick={() => setEditing(null)} className="text-xs uppercase tracking-[0.2em] text-slate-500 px-5">Cancel</button>
              <button onClick={() => onSave(editing)} data-testid="team-save" className="btn-gold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeam;
